import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import {
	getFiltersForSpendByMonth,
	getSpendComparisonByMonth,
} from 'utils/apiRequests';
import { FONTSTYLE } from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { colors } from '@alixpartners/ui-components';
import { renderToString } from 'react-dom/server';
import {
	TooltipColor,
	TooltipContainer,
	TooltipItem,
	TooltipItemName,
	TooltipItemTitle,
	TooltipItemValue,
} from './DashboardChart.styles';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import { isNull } from 'lodash';
import { getDateFormat } from 'CategoryManagement/utils';

const pageSize = 15;

const SpendByMonth = createContext();

export const SpendByMonthProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const initailChartOptions = {
		chart: { type: 'column' },
		title: { text: '' },
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		loading: {
			hideDuration: 500,
			showDuration: 500,
			labelStyle: {
				fontWeight: 'normal',
				letterSpacing: '0.3px',
			},
		},
		xAxis: {
			categories: [],
			title: {
				text: ``,
				style: { ...FONTSTYLE },
			},
			scrollbar: {
				enabled: true,
			},
			lineColor: colors.black,
			gridLineWidth: 0,
			labels: {
				style: {
					fontSize: '9px',
				},
			},
			// labels: {
			// 	formatter: function() {
			// 		if (typeof this.value !== 'number') {
			// 			if (this.value === undefined) {
			// 				return '';
			// 			} else {
			// 				if (this.value.length > 0) {
			// 					return `${this.value.substring(0, 4)}...`;
			// 				}
			// 			}
			// 		}
			// 	},
			// 	style: { ...FONTSTYLE },
			// },
		},
		yAxis: {
			gridLineWidth: 0,
			lineColor: colors.black,
			lineWidth: 1,
			labels: {
				formatter: function() {
					return formatSpendWithDecimalAndSymbol(
						this.value,
						currencySymbol,
						hideDecimals,
						0
					).toUpperCase();
				},
				style: {
					...FONTSTYLE,
				},
			},
			title: {
				text: 'Spend',
				style: {
					...FONTSTYLE,
					textOverflow: 'ellipsis',
				},
			},
		},
		legend: {
			enabled: true,
		},
		tooltip: {
			shared: true,
			useHTML: true,
			backgroundColor: colors.white,
			borderWidth: 0,
			outside: true,
			formatter: function() {
				return renderToString(
					<TooltipContainer>
						<TooltipItemTitle>{this.x}</TooltipItemTitle>
						{this.points.map(item => {
							return (
								<React.Fragment
									key={`${item.point.series.name}-${item.point.y}`}
								>
									<TooltipItem>
										<span>
											<TooltipColor color={item.color} />
											<TooltipItemName>
												{item.point.series.name}
											</TooltipItemName>
										</span>
										<TooltipItemValue>
											{formatSpendWithDecimalAndSymbol(
												item.point.y,
												currencySymbol,
												hideDecimals,
												2
											).toLocaleUpperCase()}
										</TooltipItemValue>
									</TooltipItem>
									<br />

									{item.point.series.userOptions.tooltip?.map(
										(tooltipItem, index) => {
											if (tooltipItem[item.key]) {
												return (
													<div key={`${index}${item.key}`}>
														<table>
															<thead>
																<tr>
																	<th style={{ paddingRight: '20px' }}>
																		PO Cost Centre
																	</th>
																	<th>Spend</th>
																</tr>
															</thead>
															<tbody>
																{tooltipItem[item.key].map(
																	({ pO_COST_CENTRE, spend }) => (
																		<tr key={pO_COST_CENTRE}>
																			<td style={{ paddingRight: '20px' }}>
																				<ul
																					style={{
																						listStyleType: 'disc',
																						marginLeft: '15px',
																					}}
																				>
																					<li>
																						{pO_COST_CENTRE
																							? pO_COST_CENTRE
																							: 'NA'}
																					</li>
																				</ul>
																			</td>
																			<td>
																				{formatSpendWithDecimalAndSymbol(
																					spend,
																					currencySymbol,
																					hideDecimals,
																					2
																				).toLocaleUpperCase()}
																			</td>
																		</tr>
																	)
																)}
															</tbody>
														</table>
													</div>
												);
											} else {
												return null;
											}
										}
									)}
								</React.Fragment>
							);
						})}
					</TooltipContainer>
				);
			},
			borderRadius: 2,
		},
		plotOptions: {
			column: {
				pointPadding: 0.2,
				borderWidth: 0,
			},
		},
		series: [],
		colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
	};
	const {
		// currentSelectCategory,
		dateRangeData,
		isFetching,
		isDirect,
		isSourceable,
		taxonomyType,
	} = useContext(CategoryManagementContext);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);

	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);

	const filterInitialOptions = {
		category_1: {
			options: [],
			selectedOption: [],
		},
		category_2: {
			options: [],
			selectedOption: [],
		},
		category_3: {
			options: [],
			selectedOption: [],
		},
		category_4: {
			options: [],
			selectedOption: [],
		},
		harmonized_vendor: {
			options: [],
			selectedOption: [],
		},
		dateRange: {
			srcRange: dateRangeData.srcRange,
			stateRange: dateRangeData.stateRange,
			dateFrom: dateRangeData.dateFrom,
			dateTo: dateRangeData.dateTo,
		},
	};

	const [filterData, setFilterData] = useState(filterInitialOptions);
	const [isFilterApplied, setIsFilterApplied] = useState(false);
	const [options, setOptions] = useState(initailChartOptions);

	const checkAndFormat = data => {
		return data.filter(x => x !== null).map(x => {
			return { label: x, value: x };
		});
	};

	const handleDateRangeChange = value => {
		const minMaxDates = getDateFormat(value);
		setFilterData(prevState => ({
			...prevState,
			dateRange: {
				...prevState.dateRange,
				stateRange: value,
				...minMaxDates,
			},
		}));
	};

	const applyFilter = () => {
		setIsLoading(true);
		setIsFilterApplied(true);
		const newFilters = {
			category1: filterData.category_1?.selectedOption,
			category2: filterData.category_2?.selectedOption,
			category3: filterData.category_3?.selectedOption,
			category4: filterData.category_4?.selectedOption,
			HARMONIZED_VENDOR_NAME: filterData.harmonized_vendor?.selectedOption,
		};
		const payload = {
			dateFrom: filterData.dateRange.dateFrom,
			dateTo: filterData.dateRange.dateTo,
			isSourceable: isSourceable,
			aptaxonomy: isDirect,
			taxonomyType: taxonomyType,
			...newFilters,
		};
		// const payload={category1:["Corporate Services"],isSourceable:false,aptaxonomy:true,dateFrom:"2022-01-01",dateTo:"2024-12-31",taxonomyType:2,HARMONIZED_VENDOR_NAME:["AMPATH LANORATORIES"]}
		getSpendComparisonByMonth(payload)
			.then(response => {
				if (response && response.length > 0) {
					const sortedData = response.sort((a, b) => {
						if (a.pO_CALENDAR_YEAR !== b.pO_CALENDAR_YEAR) {
							return a.pO_CALENDAR_YEAR - b.pO_CALENDAR_YEAR;
						}
						return a.pO_CALENDAR_MONTH - b.pO_CALENDAR_MONTH;
					});
					setData(sortedData);
					setIsLoading(false);
				} else {
					setIsLoading(false);
					setData([]);
				}
			})
			.catch(() => setIsLoading(false));
	};
	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setOptions(initailChartOptions);
				setFilterData(filterInitialOptions);
				setIsFilterApplied(false);
				// setIsLoading(true);
				let requestBody = {
					isSourceable: isSourceable,
					aptaxonomy: isDirect,
					taxonomyType: taxonomyType,
				};
				getFiltersForSpendByMonth(requestBody)
					.then(response => {
						if (response.succeeded) {
							if (!isNull(response.data)) {
								if (Object.keys(response.data).length > 0) {
									const formatedCategory1Options = checkAndFormat(
										response.data.category1
									);
									const formatedCategory2Options = checkAndFormat(
										response.data.category2
									);
									const formatedCategory3Options = checkAndFormat(
										response.data.category3
									);
									const formatedCategory4Options = checkAndFormat(
										response.data.category4
									);
									const formatedHarmonizedVendorOptions = checkAndFormat(
										response.data.harmonizeD_VENDOR_NAME
									);
									setFilterData(prevState => ({
										...prevState,

										category_1: {
											...prevState.category_1,
											options: formatedCategory1Options,
										},
										category_2: {
											...prevState.category_2,
											options: formatedCategory2Options,
										},
										category_3: {
											...prevState.category_3,
											options: formatedCategory3Options,
										},
										category_4: {
											...prevState.commodity_Code,
											options: formatedCategory4Options,
										},
										harmonized_vendor: {
											...prevState.commodity_Family,
											options: formatedHarmonizedVendorOptions,
										},
										dateRange: {
											...prevState.dateRange,
											srcRange: dateRangeData.srcRange,
											stateRange: dateRangeData.stateRange,
											dateFrom: dateRangeData.dateFrom,
											dateTo: dateRangeData.dateTo,
										},
									}));
								}
							}
						}
					})
					.catch(error => {
						console.log(error, 'Error Message');
					});

				// const newFilters = {
				// 	category1: filterData.category_1?.selectedOption,
				// 	category2: filterData.category_2?.selectedOption,
				// 	category3: filterData.category_3?.selectedOption,
				// 	category4: filterData.category_4?.selectedOption,
				// 	harmonizeD_VENDOR_NAME: filterData.harmonized_vendor?.selectedOption,
				// };
				// const payload = {
				// 	dateFrom: dateFrom,
				// 	dateTo: dateTo,
				// 	isSourceable: isSourceable,
				// 	aptaxonomy: isDirect,
				// 	taxonomyType: taxonomyType,
				//     ...newFilters,
				// };
				// const payload = {
				// 	category1: ['Corporate Services'],
				// 	isSourceable: isSourceable,
				// 	aptaxonomy: isDirect,
				// 	dateFrom: dateFrom,
				// 	dateTo: dateTo,
				// 	taxonomyType: taxonomyType,
				// 	HARMONIZED_VENDOR_NAME: ['AMPATH LANORATORIES'],
				// };
				// getSpendComparisonByMonth(payload)
				// 	.then(response => {
				// 		if (response && response.length > 0) {
				// 			const sortedData = response.sort((a, b) => {
				// 				if (a.pO_CALENDAR_YEAR !== b.pO_CALENDAR_YEAR) {
				// 					return a.pO_CALENDAR_YEAR - b.pO_CALENDAR_YEAR;
				// 				}
				// 				return a.pO_CALENDAR_MONTH - b.pO_CALENDAR_MONTH;
				// 			});
				// 			setData(sortedData);
				// 			setIsLoading(false);
				// 		} else {
				// 			setIsLoading(false);
				// 		}
				// 	})
				// 	.catch(() => setIsLoading(false));
			}
		}, // eslint-disable-next-line
		[
			dateRangeData,
			isSettingsFetching,
			isFetching,
			isDirect,
			isSourceable,
			taxonomyType,
		]
	);

	useEffect(
		() => {
			if (data.length > 0) {
				// const firstPageIndex = (currentPage - 1) * pageSize;
				// const lastPageIndex = firstPageIndex + pageSize;
				// const batchRecords = data.slice(firstPageIndex, lastPageIndex);
				let months = [
					'January',
					'February',
					'March',
					'April',
					'May',
					'June',
					'July',
					'August',
					'September',
					'October',
					'November',
					'December',
				];
				let tempData = [];
				for (let i = 0; i < data.length; i++) {
					let tempObj = {
						monthName: `${months[data[i].pO_CALENDAR_MONTH - 1]} ${
							data[i].pO_CALENDAR_YEAR
						}`,
						tooltipData: {
							[`${months[data[i].pO_CALENDAR_MONTH - 1]} ${
								data[i].pO_CALENDAR_YEAR
							}`]: data[i].tooltip,
						},
						...data[i],
					};
					tempData.push(tempObj);
				}
				const categoriesData = tempData.map(x => x.monthName);

				const seriesData = [
					{
						name: 'Monthly Spend',
						data: tempData.map(x => x.totalSpend),
						tooltip: tempData.map(x => x.tooltipData),
						additionalInfo: 'monthlySpend',
					},
					// {
					// 	name: 'Previous Year Spend',
					// 	data: batchRecords.map(x => x.previousYearSpend),
					// 	additionalInfo: 'previousYearSpend',
					// },
				];
				setOptions(prevState => ({
					...prevState,
					xAxis: {
						...prevState.xAxis,
						categories: categoriesData,
					},
					series: seriesData,
				}));
			} else {
				setOptions(initailChartOptions);
			}
		}, // eslint-disable-next-line
		[data, currentPage]
	);

	return (
		<SpendByMonth.Provider
			value={{
				data,
				isLoading,
				options,
				currentPage,
				setCurrentPage,
				pageSize,
				filterData,
				setFilterData,
				applyFilter,
				isFilterApplied,
				setIsFilterApplied,
				handleDateRangeChange,
			}}
		>
			{children}
		</SpendByMonth.Provider>
	);
};

export const useSpendByMonthContext = () => {
	return useContext(SpendByMonth);
};
